import { useEffect, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, useMediaQuery } from "@mui/material";
import { getPermitForResponsibleView } from "../../confirmPermits/permitApi";
import Preloader from "../../Preloader";
import Translate from "../../../utils/Translate";
import { formatDate } from "../../../utils/dateTimeFormat";
import { AccessTime, Check, CheckCircleOutline, WarningAmber } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { accessKeys, hasAccess, hasSomeAccess } from "../../../utils/userAccess";
import { useApi } from "../../../utils/Api";

export default function PermitDetailsDialog({ actorPermitId, permit: permitProp, dataId, onClose }) {
  if ((!!actorPermitId || !!dataId) && !!permitProp) {
    // Use either but not both
    throw new Error("Invalid props");
  }

  const [permit, setPermit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const closeAndReload = () => {
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const smallScreen = useMediaQuery("(max-width:700px)");
  const fieldsRowClassName = "permitDetailsRow" + (smallScreen ? " small" : "");
  const signatureRowClassName = "permitDetailsSignaturesContainer" + (smallScreen ? " small" : "");

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      let response = { isSuccessful: false };
      if (hasAccess(accessKeys.actorPermitResponsibleView)) {
        response = await getPermitForResponsibleView(actorPermitId, dataId);
      } else if (hasAccess(accessKeys.digitalLicensesHolder)) {
        response = await api.fetch(
          `${import.meta.env.VITE_MAIN_URL}v1/digital/permits/${actorPermitId}/detailed?dataId=${dataId}`,
          false,
          "GET",
        );
      } else if (hasSomeAccess(accessKeys.actorPermitSignHolder, accessKeys.actorPermitSignResponsible)) {
        if (!permitProp) {
          throw new Error("Missing prop");
        }
      } else {
        throw new Error("Not supported");
      }

      if (response.isSuccessful) {
        setPermit(response.actorPermit);
      } else if (!!permitProp) {
        setPermit(permitProp);
      }
      setIsLoading(false);
    }
    init();
  }, [actorPermitId, dataId, permitProp, api]);

  function getValidTime(permit) {
    if (!permit.validFrom) {
      return "";
    }

    var validFrom = formatDate(permit.validFrom);
    var validTo = permit.validTo ? formatDate(permit.validTo) : Translate.get("UntilFurtherNotice");
    return `${validFrom} - ${validTo}`;
  }

  function getSignatureDate(date) {
    return date ? formatDate(date) : Translate.get("WaitingToBeSigned");
  }

  if (isLoading || !permit) {
    return <Preloader />;
  }

  return (
    <>
      <DialogTitle>{`${Translate.get("DrivingPermit")} ${permit.skillGroupDescription.toLowerCase()}`}</DialogTitle>
      <DialogContent>
        <div className="permitDetailsContainer">
          <div className="permitDetailsTopContainer">
            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("Name")}</div>
                <div>{permit.actorName ? permit.actorName : "—"}</div>
              </div>

              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("PersonalNo")}</div>
                <div>{permit.actorOrgNo ? permit.actorOrgNo : "—"}</div>
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("ActorType_Client")}</div>
                <div>{permit.parentActorName ? permit.parentActorName : "—"}</div>
              </div>

              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("EmployeeNo")}</div>
                <div>{permit.externalActorNo ? permit.externalActorNo : "—"}</div>
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("ValidTime")}</div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  {getValidTime(permit) ? getValidTime(permit) : "—"}
                  {permit.validTo && new Date(permit.validTo) < new Date() && <WarningAmber className="warningColor" />}
                </div>
              </div>

              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("ActorPermitId")}</div>
                <div>{permit.actorPermitId ? permit.actorPermitId : "—"}</div>
              </div>
            </div>

            <div>
              <div className="permitDetailsFieldTitle">{Translate.get("IsPermittedToDrive")}</div>
              <div className="permitDetailsSkillsColumns">
                {smallScreen || permit.skills.length <= 1 ? (
                  <SkillList skills={permit.skills} />
                ) : (
                  <>
                    <SkillList skills={permit.skills.slice(0, Math.ceil(permit.skills.length * 0.5))} />
                    <SkillList skills={permit.skills.slice(Math.ceil(permit.skills.length * 0.5))} />
                  </>
                )}
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("WorkTasks")}</div>
                <div>{permit.task ? permit.task : "—"}</div>
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("InAreaOf")}</div>
                <div>{permit.area ? permit.area : "—"}</div>
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <div className="permitDetailsFieldTitle">{Translate.get("OtherEquipment")}</div>
                <div>{permit.otherEquipment ? permit.otherEquipment : "—"}</div>
              </div>
            </div>

            <div className={fieldsRowClassName}>
              <div className="permitDetailsField">
                <Box height={60} alignItems="top">
                  <Box display="flex">
                    <CheckCircleOutline />
                    &nbsp;&nbsp;<span>{Translate.get("AwareOfRules")}</span>
                  </Box>
                </Box>
              </div>

              <div className="permitDetailsField">
                <Box height={60} alignItems="top">
                  <Box display="flex">
                    <WarningAmber />
                    &nbsp;&nbsp;<span>{Translate.get("MayBeRevoked")}</span>
                  </Box>
                </Box>
              </div>
            </div>
          </div>

          <div>
            <div className="permitDetailsTitleLogoContainer">
              <div className="permitDetailsFieldTitle">{Translate.get("Signatures")}</div>
              <img src="/static/BankID_logo.svg" />
            </div>
            <div className={signatureRowClassName}>
              <div className={"permitDetailsSignatureBox" + (permit.responsibleSignatureDate ? " signed" : "")}>
                <div className="permitDetailsSignatureBoxText">
                  <div>{Translate.get("Responsible")}</div>
                  <div>
                    {!!permit.responsibleActorName && !!permit.responsibleTitle
                      ? `${permit.responsibleActorName} / ${permit.responsibleTitle}`
                      : (permit.responsibleActorName ?? permit.responsibleTitle ?? "—")}
                  </div>
                  <div>
                    {permit.responsibleSignatureDate
                      ? `${Translate.get("Signed")} ${getSignatureDate(permit.responsibleSignatureDate)}`
                      : `${Translate.get("WaitingToBeSigned")}...`}
                  </div>
                </div>
                <div>{permit.responsibleSignatureDate ? <Check /> : <AccessTime />}</div>
              </div>

              <div className={"permitDetailsSignatureBox" + (permit.signatureDate ? " signed" : "")}>
                <div className="permitDetailsSignatureBoxText">
                  <div>{Translate.get("Holder")}</div>
                  <div>{permit.actorName ?? "—"}</div>
                  <div>
                    {permit.signatureDate
                      ? `${Translate.get("Signed")} ${getSignatureDate(permit.signatureDate)}`
                      : `${Translate.get("WaitingToBeSigned")}...`}
                  </div>
                </div>
                <div>{permit.signatureDate ? <Check /> : <AccessTime />}</div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose ?? closeAndReload}>{Translate.get("Close")}</Button>
      </DialogActions>
    </>
  );
}

function SkillList({ skills }) {
  return (
    <div className="permitDetailsSkillsContainer">
      {skills.map((s) => (
        <div key={s.skillId} className="permitDetailsSkill">
          <img src={s.imageUrl} style={{ height: "20px" }} />
          <div>{`${s.shortCode} - ${s.description}`}</div>
        </div>
      ))}
    </div>
  );
}
